import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'actionMessage'
	| 'actionMessageLiveDoc'
	| 'spaceSelectorLabel'
	| 'pageContentPlaceholder'
	| 'publishPageButton'
	| 'createLiveDocButton'
	| 'confluencePageCreateActionCardTitle';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		actionMessage: {
			id: 'ai-mate.message-actions.confluence-page-create.action-message',
			defaultMessage:
				"Here's the page content I suggest adding to the space {spaceName}. Take a look over it, then select <bold>Publish page</bold> when you're ready.",
			description:
				'Message text displayed in an action card within the AI conversation interface when the AI suggests creating a new Confluence page. Shown as the main message text in the action card body. The spaceName placeholder will be substituted with the target Confluence space name (e.g., "Engineering Team Space"). The <bold> tags will be rendered as bold text for "Publish page". The purpose is to present the AI-generated page content to the user for review and provide clear instructions on how to proceed with publishing the page.',
		},
		actionMessageLiveDoc: {
			id: 'ai-mate.message-actions.confluence-page-create.action-message-live-doc',
			defaultMessage:
				"Here's the live doc content I suggest adding to the space {spaceName}. Take a look over it, then select <bold>Create live doc</bold> when you're ready.",
			description:
				'Message text displayed in an action card within the AI conversation interface when the AI suggests creating a new Confluence live doc (collaborative document). Shown as the main message text in the action card body. The spaceName placeholder will be substituted with the target Confluence space name (e.g., "Engineering Team Space"). The <bold> tags will be rendered as bold text for "Create live doc". The purpose is to present the AI-generated live doc content to the user for review and provide clear instructions on how to proceed with creating the live doc.',
		},
		spaceSelectorLabel: {
			id: 'ai-mate.message-actions.confluence-page-create.space-selector-label',
			defaultMessage: 'Space',
			description:
				'Label text displayed above or next to a space selector dropdown in the Confluence page creation action card within the AI conversation interface. Shown as a form field label. The purpose is to identify the space selector control, which allows users to choose which Confluence space the page will be created in.',
		},
		pageContentPlaceholder: {
			id: 'ai-mate.message-actions.confluence-page-create.page-content-placeholder',
			defaultMessage: 'Write your page content here',
			description:
				'Placeholder text displayed in a rich text editor or text input field within the Confluence page creation action card in the AI conversation interface. Shown as placeholder text when the input field is empty. The purpose is to guide users on what they should enter in the page content input field, prompting them to write or edit the page content that will be published to Confluence.',
		},
		publishPageButton: {
			id: 'ai-mate.message-actions.confluence-page-create.publish-page-button',
			defaultMessage: 'Publish page',
			description:
				'Button label displayed on the primary action button in the Confluence page creation action card within the AI conversation interface. Shown as the text on a button at the bottom of the action card. When clicked, it publishes the page to the selected Confluence space. The purpose is to provide a clear call-to-action for users to confirm and execute the page creation.',
		},
		createLiveDocButton: {
			id: 'ai-mate.message-actions.confluence-page-create.create-live-doc-button',
			defaultMessage: 'Create live doc',
			description:
				'Button label displayed on the primary action button in the Confluence live doc creation action card within the AI conversation interface. Shown as the text on a button at the bottom of the action card. When clicked, it creates a new live doc (collaborative document) in the selected Confluence space. The purpose is to provide a clear call-to-action for users to confirm and execute the live doc creation.',
		},
		confluencePageCreateActionCardTitle: {
			id: 'ai-mate.message-actions.confluence-page-create.action-card-title',
			defaultMessage: 'Create page',
			description:
				'Title text displayed at the top of an action card in the AI conversation interface when the AI suggests creating a new Confluence page. Shown as the card header title. The purpose is to immediately communicate to the user what action the card represents, which is creating a new page in Confluence.',
		},
	});

export default message;
